// src/pages/admin/AdminDashboard.jsx
import React, { useEffect, useState, useCallback } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { fetchAllUsers, fetchUserSubmissions } from "../../api/admin";

import { useNavigate } from "react-router-dom";

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const navigate = useNavigate();


  const [showModal, setShowModal] = useState(false);
  const [modalUser, setModalUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  const [submissionSortConfig, setSubmissionSortConfig] = useState({
    key: null,
    direction: "asc",
  });


  const applyFilters = useCallback(() => {
      let list = [...users];

      if (search.trim()) {
        const q = search.trim().toLowerCase();
        list = list.filter(
          (u) =>
            u.email.toLowerCase().includes(q) ||
            (u.name || "").toLowerCase().includes(q)
        );
      }

      if (filter !== "All") {
        list = list.filter((u) => u.statusLabel === filter);
      }
      

      setFiltered(list);
  }, [users, search, filter]);

  const loadAdminData = useCallback(async () => {
      try {
        setLoading(true);
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const usersFromAPI = await fetchAllUsers(idToken);
        setUsers(usersFromAPI);
        setFiltered(usersFromAPI);
      } catch (err) {
        setError("Failed to load admin data. Please try again later.");
      } finally {
        setLoading(false);
      }
  }, []);

  async function openSubmissionModal(user) {
    setModalUser(user);
    setShowModal(true);
    setSubmissions([]);
    setSubmissionsLoading(true);

    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    const items = await fetchUserSubmissions(user.sub, idToken);
    
    // Remove duplicates based on JobId
    const uniqueItems = Array.from(
      new Map(items.map(item => [item.JobId, item])).values()
    );
    
    
    setSubmissions(uniqueItems);

    setSubmissionsLoading(false);
  }

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);



  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  function sortSubmissionData(key) {
    let direction = "asc";

    if (submissionSortConfig.key === key && submissionSortConfig.direction === "asc") {
      direction = "desc";
    }

    setSubmissionSortConfig({ key, direction });

    const sorted = [...submissions].sort((a, b) => {
      let valA = a[key];
      let valB = b[key];

      // Normalize nulls
      if (valA === undefined || valA === null) valA = "";
      if (valB === undefined || valB === null) valB = "";

      // 1) DATE FIELDS
      if (key === "SubmissionTime" || key === "PullDate" || key === "CompletedAt") {
        return direction === "asc"
          ? new Date(valA) - new Date(valB)
          : new Date(valB) - new Date(valA);
      }

      // 2) STATUS FIELD
      if (key === "Status") {
        const order = ["success", "running", "failure"];
        const indexA = order.indexOf(valA);
        const indexB = order.indexOf(valB);
        return direction === "asc" ? indexA - indexB : indexB - indexA;
      }

      // 3) STRING COMPARE
      const strA = valA.toString().toLowerCase();
      const strB = valB.toString().toLowerCase();

      if (strA < strB) return direction === "asc" ? -1 : 1;
      if (strA > strB) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setSubmissions(sorted);
  }

  function submissionSortIcon(key) {
    if (submissionSortConfig.key !== key) return "↕";
    return submissionSortConfig.direction === "asc" ? "▴" : "▾";
  }

  
  function exportToCSV() {
  if (!submissions || submissions.length === 0) return;

  const headers = [
    "File Name",
    "Stem",
    "Pull Date",
    "Platform",
    "Schema",
    "Account Type",
    "Submitted At",
    "Completed At",
    "Status",
    "Status Reason"
  ];

  const rows = submissions.map(s => [
    s.UserFileName || "",
    s.Stem || "",
    s.PullDate || "",
    s.Platform || "",
    s.Schema || "",
    s.AccountType || "",
    s.SubmissionTime
      ? new Date(s.SubmissionTime).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true
        }).replace(",", "")
      : "",
    s.CompletedAt
      ? new Date(s.CompletedAt).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true
        }).replace(",", "")
      : "",
    s.Status || "",
    s.StatusReason || ""
  ]);

  const csvContent =
    "data:text/csv;charset=utf-8," +
    [headers, ...rows].map(e => e.join(",")).join("\n");

  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", `${modalUser.email}_submissions.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const sortData = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });

    const sorted = [...filtered].sort((a, b) => {
      const valA = (a[key] || "").toString().toLowerCase();
      const valB = (b[key] || "").toString().toLowerCase();

      if (key === "jobCount") {
        return direction === "asc" ? a.jobCount - b.jobCount : b.jobCount - a.jobCount;
      }

      if (key === "created") {
        return direction === "asc"
          ? new Date(a.created) - new Date(b.created)
          : new Date(b.created) - new Date(a.created);
      }

      if (valA < valB) return direction === "asc" ? -1 : 1;
      if (valA > valB) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFiltered(sorted);
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "▴" : "▾";
  };


  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-xl shadow p-6">

       <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="text-purple-800 hover:text-purple-600 text-2xl font-bold"
            title="Go back"
          >
            ←
          </button>
          <h1 className="text-3xl font-bold text-purple-800">
            Admin Dashboard
          </h1>
        </div>

        {loading && <p className="text-gray-600">Loading…</p>}
        {error && (
          <p className="text-red-600 font-semibold mt-2">{error}</p>
        )}

        {/* SEARCH + FILTER BAR */}
        <div className="flex flex-col md:flex-row justify-between gap-4 my-6">
          <input
            type="text"
            placeholder="Search by email or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border px-3 py-2 rounded-lg w-full md:w-1/3"
          />

          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border px-3 py-2 rounded-lg w-full md:w-48"
          >
            <option value="All">All</option>
            <option value="Admin">Admin</option>
            <option value="Approved">Approved</option>
            <option value="PendingApproval">Pending Approval</option>
            <option value="Unconfirmed">Unconfirmed</option>
            <option value="Disabled">Disabled</option>
          </select>
        </div>

        {/* RESULTS TABLE */}
        <div className="overflow-auto rounded-lg border border-gray-300">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-500 text-white">
              <tr>
                <th
                  className="py-2 px-4 text-left cursor-pointer"
                  onClick={() => sortData("name")}
                >
                  Name {sortIcon("name")}
                </th>

                <th
                  className="py-2 px-4 text-left cursor-pointer"
                  onClick={() => sortData("email")}
                >
                  Email {sortIcon("email")}
                </th>

                <th
                  className="py-2 px-4 text-left cursor-pointer"
                  onClick={() => sortData("statusLabel")}
                >
                  Status {sortIcon("statusLabel")}
                </th>

                <th
                  className="py-2 px-4 text-left cursor-pointer"
                  onClick={() => sortData("jobCount")}
                >
                  Jobs {sortIcon("jobCount")}
                </th>

                <th
                  className="py-2 px-4 text-left cursor-pointer"
                  onClick={() => sortData("created")}
                >
                  Created {sortIcon("created")}
                </th>
              </tr>
            </thead>


            <tbody>
              {filtered.map((u) => (
                <tr
                  key={u.sub}
                  className="border-b hover:bg-gray-50 transition"
                >
                  <td className="py-2 px-4">{u.name || "—"}</td>
                  <td className="py-2 px-4">{u.email}</td>
                  <td className="py-2 px-4">
                    <span
                      className={
                        "px-2 py-1 rounded text-xs font-semibold " +
                        (u.statusLabel === "Admin"
                          ? "bg-blue-100 text-blue-700"
                          : u.statusLabel === "Approved"
                          ? "bg-green-100 text-green-700"
                          : u.statusLabel === "PendingApproval"
                          ? "bg-yellow-100 text-yellow-700"
                          : u.statusLabel === "Unconfirmed"
                          ? "bg-gray-200 text-gray-700"
                          : "bg-red-100 text-red-700")
                      }
                    >
                      {u.statusLabel}
                    </span>
                  </td>

                  <td
                    className="py-2 px-4 text-purple-700 font-semibold cursor-pointer hover:underline"
                    onClick={() => openSubmissionModal(u)}
                  >
                    {u.jobCount}
                  </td>


                  <td className="py-2 px-4">
                    {new Date(u.created).toLocaleDateString()}
                  </td>
                </tr>
              ))}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    className="p-4 text-center text-gray-500"
                  >
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* SUBMISSION DETAILS MODAL */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">

              {/* FIXED HEADER */}
              <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10">

                <h2 className="text-xl font-bold text-purple-800">
                  Submissions for {modalUser?.email}
                </h2>

                <div className="flex items-center gap-3">
                  {/* EXPORT BUTTON */}
                  <button
                    onClick={exportToCSV}
                    className="bg-green-600 text-white px-4 py-1.5 rounded-lg text-sm font-semibold hover:bg-green-700 transition"
                  >
                    Export CSV
                  </button>

                  {/* CLOSE BUTTON */}
                  <button
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                    onClick={() => setShowModal(false)}
                  >
                    ×
                  </button>
                </div>

              </div>


              {/* SCROLLABLE TABLE CONTENT */}
              <div className="overflow-auto px-6 py-4">
                <table className="min-w-full text-sm table-fixed border border-gray-300 bg-white">
                  <thead>
                    <tr className="bg-gray-500 border-b border-gray-300 text-left text-white font-semibold">

                      <th
                        className="py-2 px-3 cursor-pointer"
                        onClick={() => sortSubmissionData("UserFileName")}
                      >
                        File Name {submissionSortIcon("UserFileName")}
                      </th>

                      <th
                        className="py-2 px-3 cursor-pointer"
                        onClick={() => sortSubmissionData("Stem")}
                      >
                        Stem {submissionSortIcon("Stem")}
                      </th>

                      <th
                        className="py-2 px-3 cursor-pointer"
                        onClick={() => sortSubmissionData("PullDate")}
                      >
                        Pull Date {submissionSortIcon("PullDate")}
                      </th>

                      <th
                        className="py-2 px-3 cursor-pointer"
                        onClick={() => sortSubmissionData("Platform")}
                      >
                        Platform {submissionSortIcon("Platform")}
                      </th>

                      <th
                        className="py-2 px-3 cursor-pointer"
                        onClick={() => sortSubmissionData("Schema")}
                      >
                        Schema {submissionSortIcon("Schema")}
                      </th>

                      <th
                        className="py-2 px-3 cursor-pointer"
                        onClick={() => sortSubmissionData("AccountType")}
                      >
                        Account Type {submissionSortIcon("AccountType")}
                      </th>

                      <th
                        className="py-2 px-3 cursor-pointer"
                        onClick={() => sortSubmissionData("SubmissionTime")}
                      >
                        Submitted At {submissionSortIcon("SubmissionTime")}
                      </th>

                      <th
                        className="py-2 px-3 cursor-pointer"
                        onClick={() => sortSubmissionData("CompletedAt")}
                      >
                        Completed At {submissionSortIcon("CompletedAt")}
                      </th>


                      <th
                        className="py-2 px-3 cursor-pointer"
                        onClick={() => sortSubmissionData("Status")}
                      >
                        Status {submissionSortIcon("Status")}
                      </th>

                    </tr>
                  </thead>


                  <tbody>
                    {submissions.map((s) => (
                      <tr key={s.JobId} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="py-2 px-3 whitespace-normal break-all">
                        {s.UserFileName || "—"}
                      </td>

                        <td className="py-2 px-3 whitespace-normal break-all">{s.Stem || "—"}</td>
                        <td className="py-2 px-3">{s.PullDate || "—"}</td>
                        <td className="py-2 px-3">{s.Platform || "—"}</td>
                        <td className="py-2 px-3">{s.Schema || "—"}</td>
                        <td className="py-2 px-3">{s.AccountType || "—"}</td>
                        <td className="py-2 px-3">
                          {s.SubmissionTime
                            ? new Date(s.SubmissionTime).toLocaleString("en-US", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              hour12: true
                            }).replace(",", "")   
                            : "—"}
                        </td>

                        <td className="py-2 px-3">
                          {s.CompletedAt
                            ? new Date(s.CompletedAt).toLocaleString("en-US", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                                hour12: true
                              }).replace(",", "")
                            : "—"}
                        </td>
                            
                        <td className="py-2 px-3">
                          <span
                            className={
                              "px-2 py-1 rounded text-xs font-semibold " +
                              (s.Status === "success"
                                ? "bg-green-100 text-green-700"
                                : s.Status === "running"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-red-100 text-red-700")  // failure
                            }
                          >
                            {s.Status || "failure"}
                          </span>
                        </td>


                      </tr>
                    ))}

                    {submissionsLoading ? (
                      <tr>
                        <td colSpan="9" className="py-4 text-center text-gray-500">
                          Loading submissions…
                        </td>
                      </tr>
                    ) : submissions.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="py-4 text-center text-gray-500">
                          No submissions found.
                        </td>
                      </tr>
                    ) : null}

                  </tbody>
                </table>
              </div>

            </div>
          </div>
        )}





      </div>
    </div>
  );
}